import type { EventCapturer } from '#src/analytics/analytics_event.ts';
import { useTrackEventOnce } from '#src/analytics/react/use_track_event_once.ts';
import {
  WishlistLinkClickEvent,
  WishlistMatchListViewEvent,
} from '#src/wishlists/analytics/wishlist_analytics_events.ts';
import type { MatchWishlistItem } from '@packages/contracts/wishlist';
import { exists } from '@packages/primitives/exists';
import { AnchorAsButton } from '@packages/ui/base/button/anchor_as_button';
import { Card } from '@packages/ui/base/card/card';
import { useCallback, useMemo } from 'react';

export interface MatchWishlistCardProps {
  captureEvent: EventCapturer;
  matchName: string;
  items: MatchWishlistItem[];
}

export const MatchWishlistCard = ({ matchName, items, captureEvent }: MatchWishlistCardProps) => {
  const affiliatedItemCount = useMemo(() => getAffiliatedItemCount(items), [items]);
  const showEmptyState = items.length === 0;
  const showItems = items.length > 0;

  const trackMatchListView = useCallback(() => {
    captureEvent(WishlistMatchListViewEvent, {
      itemCount: items.length,
      affiliatedItemCount,
    });
  }, [affiliatedItemCount, captureEvent, items.length]);

  useTrackEventOnce({
    isEnabled: items.length > 0,
    onTrack: trackMatchListView,
  });

  return (
    <Card className="py-6">
      {/* ---- Header ---- */}
      <h2 className="mb-5 text-xl font-bold">What {matchName} wished for</h2>

      {/* ---- Content ---- */}
      {showEmptyState && <MatchWishlistEmptyState matchName={matchName} />}

      {showItems && (
        <div className="flex flex-col gap-3">
          {items.map(item => (
            <MatchWishlistItemRow
              key={item.id}
              item={item}
              onLinkClick={() =>
                captureEvent(WishlistLinkClickEvent, {
                  retailerName: item.retailer?.name ?? null,
                  retailerId: item.retailer?.id ?? null,
                })
              }
            />
          ))}
        </div>
      )}
    </Card>
  );
};

const MatchWishlistEmptyState = ({ matchName }: { matchName: string }) => {
  return (
    <div className="border-muted-foreground/30 flex flex-col items-center rounded-2xl border border-dashed px-4 py-6 text-center">
      <p className="text-muted-foreground text-sm">
        {matchName} hasn&apos;t added anything yet. You&apos;ll have to get creative!
      </p>
    </div>
  );
};

const MatchWishlistItemRow = ({
  item,
  onLinkClick,
}: {
  item: MatchWishlistItem;
  onLinkClick: () => void;
}) => {
  const url = item.url ?? null;
  const noteText = item.note;
  const retailerName = item.retailer?.name ?? null;
  const hasLink = exists(url);

  const linkLabel = exists(retailerName) ? `Buy on ${retailerName}` : 'Buy now';

  return (
    <div className="bg-primary/6 ring-primary/30 overflow-hidden rounded-2xl ring-1">
      {/* ---- Content ---- */}
      <div className="px-6 py-4">
        <p className="font-bold wrap-break-word">{item.title}</p>
        {noteText && (
          <p className="text-foreground/60 mt-2 text-sm text-pretty wrap-break-word whitespace-pre-wrap">
            {noteText}
          </p>
        )}
        {hasLink && (
          <div className="mt-3 flex justify-end">
            <AnchorAsButton
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onLinkClick}
              variant="secondary"
              size="xs"
            >
              <span>{linkLabel}</span>
            </AnchorAsButton>
          </div>
        )}
      </div>
    </div>
  );
};

function getAffiliatedItemCount(items: MatchWishlistItem[]): number {
  return items.filter(item => exists(item.url) && exists(item.retailer)).length;
}
